import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { ChartResult, ChartSpec } from '@employ-engage/data';
import { useState } from 'react';
import DashboardTileCard from './dashboard-tile';
import {
  DashboardTile,
  moveTileById,
  tileClassName,
  TileHeight,
  TileSize,
} from './tile-layout';

export interface DashboardGridProps {
  tiles: DashboardTile[];
  titleFor: (tile: DashboardTile) => string;
  specFor: (tile: DashboardTile) => ChartSpec | undefined;
  resultFor: (tile: DashboardTile) => ChartResult | null | undefined;
  isMissing: (tile: DashboardTile) => boolean;
  /** Why a dashboard-wide override did not reach this tile, if it did not. */
  overrideNoteFor?: (tile: DashboardTile) => string | null;
  /** The small "Created by …" line under a tile's title, if there is one. */
  bylineFor?: (tile: DashboardTile) => string | null;
  /** A value clicked on a tile, for cross-filtering. Omit to make tiles inert. */
  onSelect?: (tile: DashboardTile, category: string) => void;
  loading?: boolean;
  error?: string | null;
  onChange: (tiles: DashboardTile[]) => void;
}

/**
 * The dashboard's layout mechanics, all in one place.
 *
 * Size is a token rather than free pixels, which keeps mobile correct without
 * a second layout to maintain. If this ever needs true drag-to-resize, only
 * this file changes: {order, size, height} converts mechanically to
 * {x, y, w, h}, and nothing outside here knows how layout works.
 */
export function DashboardGrid({
  tiles,
  titleFor,
  specFor,
  resultFor,
  isMissing,
  overrideNoteFor,
  bylineFor,
  onSelect,
  loading,
  error,
  onChange,
}: DashboardGridProps) {
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const sensors = useSensors(
    // A small threshold so clicking a chart legend is not read as a drag.
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    }),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = (event: DragEndEvent) => {
    setDraggingId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onChange(moveTileById(tiles, String(active.id), String(over.id)));
  };

  const dragging = tiles.find((tile) => tile.id === draggingId);

  return (
    <DndContext
      sensors={sensors}
      onDragStart={(event: DragStartEvent) =>
        setDraggingId(String(event.active.id))
      }
      onDragCancel={() => setDraggingId(null)}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={tiles.map((t) => t.id)}
        strategy={rectSortingStrategy}
      >
        <div className="grid grid-cols-12 gap-5">
          {tiles.map((tile) => (
            <div key={tile.id} className={tileClassName(tile.size)}>
              <DashboardTileCard
                tile={tile}
                title={titleFor(tile)}
                spec={specFor(tile)}
                result={resultFor(tile)}
                missing={isMissing(tile)}
                overrideNote={overrideNoteFor?.(tile) ?? null}
                byline={bylineFor?.(tile) ?? null}
                onSelect={
                  onSelect ? (category) => onSelect(tile, category) : undefined
                }
                loading={loading}
                error={error}
                onSizeChange={(size: TileSize) =>
                  onChange(
                    tiles.map((t) => (t.id === tile.id ? { ...t, size } : t))
                  )
                }
                onHeightChange={(height: TileHeight) =>
                  onChange(
                    tiles.map((t) => (t.id === tile.id ? { ...t, height } : t))
                  )
                }
                onRemove={() => onChange(tiles.filter((t) => t.id !== tile.id))}
              />
            </div>
          ))}
        </div>
      </SortableContext>

      {/* Keeps the dragged tile at its own size while the grid reflows
          underneath, instead of stretching into each slot it passes over. */}
      <DragOverlay>
        {dragging ? (
          <div className="rounded-lg bg-white opacity-90 shadow-xl dark:bg-dark-secondaryBackground">
            <div className="px-4 py-3 text-sm font-semibold">
              {titleFor(dragging)}
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export default DashboardGrid;
